import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  getAllTasksForDate,
  isExternalCalendarTask,
  toLocalDateStr,
} from "../utils/calendarUtils";
import useExternalCalendarEvents, { filterEventsForDate, fmtSyncedTime, PROVIDER_LABEL, PROVIDER_GLYPH } from "../hooks/useExternalCalendarEvents";
import { api } from "../api/client";
import { PRIORITY_COLORS, PRIORITY_RANK } from "../utils/priorityColors";

// ── Design tokens ─────────────────────────────────────────────────────────────
const NAVY = "#0C2D48";
const FONT = "'Raleway', sans-serif";

function priorityStrip(priority) {
  return (PRIORITY_COLORS[priority] || PRIORITY_COLORS.Medium).color;
}

const TEAM_PALETTE = ["#5352ed","#e91e8c","#f59e0b","#16a34a","#0891b2","#7c3aed","#dc2626","#059669"];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtTime(timeStr) {
  if (!timeStr) return null;
  try {
    if (String(timeStr).includes("T")) {
      return new Date(timeStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
    }
    const [h, m] = String(timeStr).split(":").map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch {
    return null;
  }
}

function sortByPriority(list) {
  return [...list].sort((a, b) => {
    const pa = PRIORITY_RANK[a.priority] ?? 2;
    const pb = PRIORITY_RANK[b.priority] ?? 2;
    if (pa !== pb) return pa - pb;
    return (a.time || "").localeCompare(b.time || "");
  });
}

function PriorityDot({ priority }) {
  const color = priorityStrip(priority);
  return (
    <span style={{
      width: 7, height: 7, borderRadius: "50%",
      background: color, flexShrink: 0, display: "inline-block", marginTop: "0.22rem",
    }} />
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ message, type = "success", onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  const bg = type === "error" ? "#dc2626" : type === "info" ? "#2563eb" : "#16a34a";
  return (
    <div style={{
      position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
      background: bg, color: "#fff", padding: "0.65rem 1.25rem", borderRadius: 10,
      fontFamily: FONT, fontWeight: 700, fontSize: "0.88rem", zIndex: 9999,
      boxShadow: "0 4px 16px rgba(0,0,0,0.18)", pointerEvents: "none",
    }}>
      {message}
    </div>
  );
}

// ── Day column ────────────────────────────────────────────────────────────────
const TASK_LIMIT = 4;

function DayColumn({
  date, allTasks, isToday, isWeekend,
  onClickColumn, onClickMore, onClickTask,
  onDragTask, onDropTask, isDragOver, teamEvents, teamMembers,
  bufferBefore = 0, bufferAfter = 0,
  syncedEvents = [],
}) {
  const regularTasks = sortByPriority(
    allTasks.filter(t => !isExternalCalendarTask(t) && !t.completed && t.source !== 'buffer')
  );
  const bufferTasks = allTasks.filter(t => t.source === 'buffer');
  const visibleTasks = regularTasks.slice(0, TASK_LIMIT);
  const hiddenCount = Math.max(0, regularTasks.length - TASK_LIMIT);
  // Block D8: buffers must appear in time order ALONGSIDE tasks, not stacked at the
  // top of the day. Merge tasks + buffers into one list ordered by time-of-day.
  const timeKey = (t) => (t.time && /^\d{1,2}:\d{2}/.test(t.time))
    ? t.time.padStart(5, '0')
    : (t.datetime ? String(t.datetime).slice(11, 16) : '99:99');
  const dayItems = [...visibleTasks, ...bufferTasks].sort((a, b) => timeKey(a).localeCompare(timeKey(b)));
  // syncedEvents replaces the old client-side calEvents — they come from the backend calendar_events table
  const calEvents = syncedEvents;

  const fullDayName = date.toLocaleDateString([], { weekday: "long" });
  const dateLabel = date.toLocaleDateString([], { day: "numeric", month: "short" });
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  // Team events for this day
  const dayTeamEvents = (teamEvents || []).filter(e => e.datetime?.slice(0, 10) === dateStr);

  return (
    <div
      onClick={onClickColumn}
      onDragOver={e => { e.preventDefault(); }}
      onDrop={e => { e.preventDefault(); e.stopPropagation(); onDropTask && onDropTask(date); }}
      onMouseEnter={e => { if (!isDragOver) e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.10)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = isToday ? "0 6px 18px rgba(12,45,72,0.13)" : "0 6px 18px rgba(0,0,0,0.05)"; }}
      style={{
        flex: 1, minWidth: 0, minHeight: 0,
        display: "flex", flexDirection: "column",
        background: isDragOver ? "#f0f4ff" : isToday ? "#fafbff" : "#ffffff",
        border: isDragOver ? `2px dashed ${NAVY}` : isToday ? `2px solid ${NAVY}` : "1px solid #e0e4e8",
        borderRadius: 10, overflow: "hidden", cursor: "pointer",
        boxShadow: isToday ? "0 6px 18px rgba(12,45,72,0.13)" : "0 6px 18px rgba(0,0,0,0.05)",
        transition: "box-shadow 0.15s, background 0.12s, border 0.12s",
      }}
    >
      {/* Header */}
      <div style={{ padding: "0.85rem 0.6rem 0.65rem", textAlign: "center", flexShrink: 0, borderBottom: "1px solid #f0f0f0" }}>
        <div style={{ fontSize: "1rem", fontWeight: 700, color: NAVY, fontFamily: "'Poppins', sans-serif", lineHeight: 1.2 }}>
          {fullDayName}
        </div>
        <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.2rem", fontFamily: FONT, lineHeight: 1 }}>
          {dateLabel}
        </div>
        <div style={{
          display: "inline-block", background: NAVY, color: "#E4E69B",
          fontSize: "0.58rem", fontWeight: 700, padding: "0.15rem 0.55rem",
          borderRadius: 20, letterSpacing: "0.07em", marginTop: "0.35rem",
          visibility: isToday ? "visible" : "hidden",
        }}>TODAY</div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0.55rem 0.55rem 0.6rem", display: "flex", flexDirection: "column" }}>

        {regularTasks.length === 0 && calEvents.length === 0 && dayTeamEvents.length === 0 && bufferTasks.length === 0 && (
          <p style={{ textAlign: "center", color: "#888", fontSize: "0.82rem", fontStyle: "italic", padding: "0.6rem 0", margin: 0, fontFamily: FONT }}>
            {isDragOver ? "Drop here to move" : "No tasks"}
          </p>
        )}

        {/* Aidey tasks + buffers, interleaved in time order (Block D8) */}
        {dayItems.map(task => task.source === 'buffer' ? (
          <div key={task.id} style={{
            padding: "0.28rem 0.4rem", borderRadius: 6, marginBottom: "0.22rem",
            background: "#f3f4f6", border: "1px dashed #d1d5db", opacity: 0.7, flexShrink: 0,
          }}>
            <div style={{ fontSize: "0.72rem", color: "#6b7280", fontFamily: FONT, fontWeight: 600 }}>
              ⏸ Buffer{task.datetime ? ` · ${fmtTime(String(task.datetime).slice(11, 16))}` : ''}
            </div>
          </div>
        ) : (
          <div
            key={task.id}
            draggable
            onDragStart={e => {
              e.stopPropagation();
              onDragTask && onDragTask(task, date);
              e.dataTransfer.effectAllowed = "move";
            }}
            onClick={e => { e.stopPropagation(); onClickTask(task); }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(12,45,72,0.10)"; e.currentTarget.style.opacity = "0.85"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; e.currentTarget.style.opacity = "1"; }}
            style={{
              display: "flex", alignItems: "flex-start", gap: "0.35rem",
              padding: "8px 10px", borderRadius: 6, marginBottom: "0.28rem",
              minHeight: 36, width: "100%", boxSizing: "border-box",
              background: "#fff", borderLeft: `3px solid ${priorityStrip(task.priority)}`,
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)", cursor: "grab",
              fontFamily: FONT, transition: "box-shadow 0.12s, opacity 0.12s", flexShrink: 0,
            }}
          >
            <PriorityDot priority={task.priority} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1a1a1a", lineHeight: 1.3, whiteSpace: "normal", wordBreak: "break-word", overflow: "visible" }}>
                {task.title}
              </div>
              {task.time && <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.1rem" }}>🕐 {fmtTime(task.time)}</div>}
            </div>
          </div>
        ))}

        {hiddenCount > 0 && (
          <button onClick={e => { e.stopPropagation(); onClickMore(); }}
            style={{ background: "none", border: "none", color: NAVY, fontSize: "0.75rem", fontWeight: 700, cursor: "pointer", textAlign: "left", padding: "0.05rem 0.4rem 0.25rem", fontFamily: FONT, opacity: 0.6, flexShrink: 0 }}>
            +{hiddenCount} more
          </button>
        )}

        {/* Synced external calendar events (read-only) */}
        {calEvents.length > 0 && (
          <>
            <div style={{ height: 1, background: "#eeeeee", margin: "0.35rem 0 0.3rem", flexShrink: 0 }} />
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "0.22rem", paddingLeft: "0.1rem", fontFamily: FONT, flexShrink: 0 }}>
              Calendar
            </div>
            {calEvents.slice(0, 4).map((ev, i) => {
              const glyph = PROVIDER_GLYPH[ev.provider] || '📅';
              const providerLabel = PROVIDER_LABEL[ev.provider] || 'Calendar';
              const timeStr = ev.startTime ? fmtSyncedTime(ev.startTime) : null;
              return (
                <div
                  key={ev.id || i}
                  title={`${ev.title || 'Event'}\n${timeStr || ''}\nSynced from ${providerLabel}`}
                  style={{ display: "flex", alignItems: "flex-start", gap: "0.3rem", padding: "0.3rem 0.4rem", borderRadius: 7, marginBottom: "0.2rem", background: "rgba(100,116,139,0.06)", border: "1px solid rgba(100,116,139,0.15)", cursor: "default", fontFamily: FONT, flexShrink: 0, userSelect: "none" }}
                >
                  <span style={{ fontSize: "0.72rem", flexShrink: 0, marginTop: "0.08rem", opacity: 0.7 }}>{glyph}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.78rem", fontWeight: 500, color: "#64748b", lineHeight: 1.3, whiteSpace: "normal", wordBreak: "break-word", overflow: "visible" }}>{ev.title || 'Untitled event'}</div>
                    {timeStr && <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: "0.05rem" }}>{timeStr}</div>}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* Team events */}
        {dayTeamEvents.length > 0 && (
          <>
            <div style={{ height: 1, background: "#eeeeee", margin: "0.35rem 0 0.3rem", flexShrink: 0 }} />
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "0.22rem", paddingLeft: "0.1rem", fontFamily: FONT, flexShrink: 0 }}>Team</div>
            {dayTeamEvents.slice(0, 4).map((ev, i) => {
              const memberIdx = (teamMembers || []).findIndex(m => m.id === ev.userId);
              const color = memberIdx >= 0 ? TEAM_PALETTE[memberIdx % TEAM_PALETTE.length] : "#888";
              return (
                <div key={ev.id || i} style={{ display: "flex", alignItems: "flex-start", gap: "0.3rem", padding: "0.28rem 0.4rem", borderRadius: 7, marginBottom: "0.2rem", background: `${color}18`, borderLeft: `3px solid ${color}`, fontFamily: FONT, flexShrink: 0 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.76rem", fontWeight: 600, color: "#333", lineHeight: 1.3, whiteSpace: "normal", wordBreak: "break-word", overflow: "visible" }}>{ev.title}</div>
                    {ev.datetime && <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.05rem" }}>{fmtTime(ev.datetime)}</div>}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main WeekView ─────────────────────────────────────────────────────────────
function WeekView({
  tasks,
  today,
  setSelectedWeekDate,
  setShowWeekModal,
  setViewTask,
  onNavigateDay,
  onAddTask,
  onRescheduleTask,
}) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" && window.matchMedia('(max-width: 767px)').matches
  );
  const [teamView, setTeamView] = useState(false);
  const [teamEvents, setTeamEvents] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [hiddenMembers, setHiddenMembers] = useState(new Set());
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimeout = useRef(null);
  const [calPrefs, setCalPrefs] = useState({
    weekStartsOn: 1,
    showWeekends: true,
    bufferBefore: 0,
    bufferAfter: 0,
  });

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    api('/api/calendar/preferences')
      .then(r => r.json())
      .then(d => setCalPrefs({
        weekStartsOn: d.weekStartsOn ?? 1,
        showWeekends: d.showWeekends ?? true,
        bufferBefore: d.bufferBefore ?? 0,
        bufferAfter: d.bufferAfter ?? 0,
      }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobileViewport(e.matches);
    setIsMobileViewport(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  // Start of week based on user preference (0 = Sunday, 1 = Monday)
  const todayWeekday = today.getDay();
  const baseMonday = new Date(today);
  baseMonday.setDate(today.getDate() - ((todayWeekday - calPrefs.weekStartsOn + 7) % 7));
  baseMonday.setHours(0, 0, 0, 0);

  const monday = new Date(baseMonday);
  monday.setDate(baseMonday.getDate() + weekOffset * 7);

  const weekdays = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
  const saturday = new Date(monday); saturday.setDate(monday.getDate() + 5);
  const sunday   = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const weekDays = [...weekdays, saturday, sunday];
  const displayDays = calPrefs.showWeekends ? weekDays : weekdays;

  // Fetch synced external calendar events for the visible week
  const weekStart = monday.toISOString();
  const weekEnd   = new Date(sunday.getTime() + 86400000).toISOString();
  const { events: syncedEvents } = useExternalCalendarEvents(weekStart, weekEnd);

  // Fetch team events when team view is on
  useEffect(() => {
    if (!teamView) { setTeamEvents([]); setTeamMembers([]); return; }
    api(`/api/calendar/team?start=${encodeURIComponent(weekStart)}&end=${encodeURIComponent(weekEnd)}`)
      .then(r => r.json())
      .then(d => {
        if (Array.isArray(d.events)) setTeamEvents(d.events);
        if (Array.isArray(d.members)) setTeamMembers(d.members);
      })
      .catch(() => {});
  }, [teamView, weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtRange = () => {
    const startDay   = monday.getDate();
    const endDay     = sunday.getDate();
    const startMonth = monday.toLocaleDateString([], { month: "short" });
    const endMonth   = sunday.toLocaleDateString([], { month: "short" });
    const year       = sunday.getFullYear();
    return startMonth === endMonth
      ? `${startDay}–${endDay} ${endMonth} ${year}`
      : `${startDay} ${startMonth} – ${endDay} ${endMonth} ${year}`;
  };

  function getDayTasks(date) {
    const localStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    // External events are passed separately via syncedEvents — no external args needed here
    return getAllTasksForDate(tasks, [], localStr, [], []);
  }

  function getDaySyncedEvents(date) {
    const localStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return filterEventsForDate(syncedEvents, localStr);
  }

  function handleDayClick(date) {
    if (onNavigateDay) {
      onNavigateDay(date);
    } else {
      setSelectedWeekDate(date);
      setShowWeekModal(true);
    }
  }

  // ── Drag to reschedule ────────────────────────────────────────────────────
  const handleDragTask = useCallback((task, fromDate) => {
    setDraggedTask({ task, fromDate });
    setDragOverDate(null);
  }, []);

  const handleDropTask = useCallback(async (toDate) => {
    if (!draggedTask) return;
    const { task } = draggedTask;

    if (isExternalCalendarTask(task)) {
      showToast("External calendar events can't be dragged — edit them in Google/Outlook directly", "info");
      setDraggedTask(null);
      setDragOverDate(null);
      return;
    }

    const fromDt = task.datetime ? new Date(task.datetime) : null;
    if (!fromDt || fromDt.toDateString() === toDate.toDateString()) {
      setDraggedTask(null);
      setDragOverDate(null);
      return;
    }

    // Optimistic: build new datetime keeping the same time-of-day
    const newStart = new Date(toDate);
    newStart.setHours(fromDt.getHours(), fromDt.getMinutes(), 0, 0);

    const fromEnd = task.deadline ? new Date(task.deadline) : new Date(fromDt.getTime() + 3600000);
    const duration = fromEnd.getTime() - fromDt.getTime();
    const newEnd = new Date(newStart.getTime() + duration);

    // Optimistic UI — mutate the task list immediately via callback
    if (onRescheduleTask) {
      onRescheduleTask(task.id, newStart.toISOString(), newEnd.toISOString());
    }

    setDraggedTask(null);
    setDragOverDate(null);

    try {
      const res = await api(`/api/tasks/${task.id}`, {
        method: 'PUT',
        body: JSON.stringify({ datetime: newStart.toISOString(), deadline: newEnd.toISOString() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to move event');
      showToast('Event moved', 'success');
    } catch (err) {
      // Revert
      if (onRescheduleTask) {
        onRescheduleTask(task.id, fromDt.toISOString(), fromEnd.toISOString());
      }
      showToast(err.message || "Could not move event", "error");
    }
  }, [draggedTask, onRescheduleTask, showToast]);

  const visibleTeamEvents = teamView
    ? teamEvents.filter(e => !hiddenMembers.has(e.userId))
    : [];

  function colProps(date) {
    const dateStr = toLocalDateStr(date);
    const jsDay = date.getDay();
    return {
      date,
      allTasks: getDayTasks(date),
      syncedEvents: getDaySyncedEvents(date),
      isToday: date.toDateString() === today.toDateString(),
      isWeekend: jsDay === 0 || jsDay === 6,
      isDragOver: dragOverDate === dateStr,
      teamEvents: visibleTeamEvents,
      teamMembers,
      onClickColumn: () => handleDayClick(date),
      onClickMore: () => { setSelectedWeekDate(date); setShowWeekModal(true); },
      onClickTask: setViewTask,
      onDragTask: handleDragTask,
      onDropTask: handleDropTask,
      bufferBefore: calPrefs.bufferBefore,
      bufferAfter: calPrefs.bufferAfter,
    };
  }

  const ROW = { display: "flex", gap: "0.75rem", height: "calc(50% - 0.5rem)", minHeight: 0 };

  const navBtnBase = {
    border: "1px solid #d0d7df", borderRadius: 8, background: "#fff",
    cursor: "pointer", fontFamily: FONT, fontWeight: 600, fontSize: "0.82rem",
    color: NAVY, padding: "8px 12px", whiteSpace: "nowrap", transition: "background 0.12s",
    flexShrink: 0,
  };

  return (
    <div
      className="week-view-container"
      style={{ padding: "1rem 16px", fontFamily: FONT, display: "flex", flexDirection: "column", height: "calc(100dvh - 4rem)", boxSizing: "border-box", overflowY: "auto", overflowX: "hidden", maxWidth: "100vw", width: "100%", gap: "0.75rem" }}
      onDragOver={e => {
        if (!draggedTask) return;
        const col = e.target.closest('[data-date]');
        if (col) setDragOverDate(col.dataset.date);
      }}
    >
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}

      {/* ── Header ── */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem", flexWrap: "wrap", gap: "8px", width: "100%" }}>
          <h2 style={{ margin: 0, fontSize: "1.25rem", color: NAVY, fontFamily: "'Poppins', sans-serif" }}>
            {weekOffset === 0 ? "This Week" : weekOffset > 0 ? "Future Week" : "Past Week"}
          </h2>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            {/* Team toggle */}
            <button
              onClick={() => setTeamView(v => !v)}
              style={{ padding: "0.38rem 0.85rem", background: teamView ? NAVY : "#f0f2f4", border: `1px solid ${teamView ? NAVY : "#d0d7df"}`, borderRadius: 8, fontWeight: 700, fontSize: "0.8rem", cursor: "pointer", fontFamily: FONT, color: teamView ? "#E4E69B" : "#555", whiteSpace: "nowrap" }}
            >
              👥 {teamView ? "Team: On" : "Team"}
            </button>
            {onAddTask && (
              <button onClick={onAddTask} style={{ padding: "0.48rem 1.05rem", background: "#E4E69B", border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", fontFamily: FONT, color: "#333", whiteSpace: "nowrap" }}>
                + Add Task
              </button>
            )}
          </div>
        </div>

        {/* Navigation row */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", marginBottom: teamView && teamMembers.length > 0 ? "0.5rem" : 0 }}>
          <button onClick={() => setWeekOffset(o => o - 1)} style={navBtnBase}
            onMouseEnter={e => { e.currentTarget.style.background = "#f0f2f4"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}>
            {isMobileViewport ? "←" : "← Previous Week"}
          </button>
          <div style={{ flex: 1, textAlign: "center", minWidth: 0 }}>
            <div style={{ fontSize: "0.875rem", fontWeight: 700, color: NAVY, lineHeight: 1.3 }}>
              Week of {fmtRange()}
            </div>
            {weekOffset !== 0 && (
              <button onClick={() => setWeekOffset(0)} style={{ marginTop: "0.2rem", background: NAVY, color: "#E4E69B", border: "none", borderRadius: 20, padding: "0.18rem 0.75rem", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT, letterSpacing: "0.04em" }}>
                This Week
              </button>
            )}
          </div>
          <button onClick={() => setWeekOffset(o => o + 1)} style={navBtnBase}
            onMouseEnter={e => { e.currentTarget.style.background = "#f0f2f4"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}>
            {isMobileViewport ? "→" : "Next Week →"}
          </button>
        </div>

        {/* Team legend + visibility chips */}
        {teamView && teamMembers.length > 0 && (
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center", paddingTop: "0.4rem" }}>
            <span style={{ fontSize: "0.72rem", color: "#888", fontWeight: 700, marginRight: "0.1rem" }}>Team:</span>
            {teamMembers.map((m, i) => {
              const color = TEAM_PALETTE[i % TEAM_PALETTE.length];
              const hidden = hiddenMembers.has(m.id);
              return (
                <button key={m.id} onClick={() => setHiddenMembers(prev => { const n = new Set(prev); hidden ? n.delete(m.id) : n.add(m.id); return n; })}
                  style={{ display: "flex", alignItems: "center", gap: "0.3rem", padding: "0.2rem 0.55rem", borderRadius: 20, border: `1.5px solid ${color}`, background: hidden ? "#f5f5f5" : `${color}18`, cursor: "pointer", fontFamily: FONT, fontSize: "0.76rem", fontWeight: 700, color: hidden ? "#aaa" : color, transition: "all 0.12s", textDecoration: hidden ? "line-through" : "none" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: hidden ? "#ccc" : color, flexShrink: 0 }} />
                  {m.name || m.email}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {isMobileViewport ? (
        <div style={{ flex: 1, overflowY: "auto", paddingBottom: "0.5rem", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {displayDays.map((date) => {
            const allDay = getDayTasks(date);
            const dayTasks = sortByPriority(
              allDay.filter(t => !isExternalCalendarTask(t) && !t.completed && t.source !== 'buffer')
            );
            const dayName = date.toLocaleDateString([], { weekday: "long" });
            const dateLabel = date.toLocaleDateString([], { month: "short", day: "numeric" });
            const isToday = date.toDateString() === today.toDateString();
            const visibleTasks = dayTasks.slice(0, 3);
            const hiddenCount = Math.max(0, dayTasks.length - 3);
            return (
              <div key={date.toISOString().slice(0, 10)} style={{ border: isToday ? `1.5px solid ${NAVY}` : "1px solid #e6e9ee", borderRadius: 10, background: "#fff", overflow: "hidden", flexShrink: 0 }}>
                <button onClick={() => handleDayClick(date)} style={{ width: "100%", border: "none", background: isToday ? "#f3f7ff" : "#f8fafc", borderBottom: "1px solid #edf1f5", padding: "0.7rem 1rem", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontFamily: FONT }}>
                  <span style={{ fontWeight: 700, color: NAVY, fontSize: "1rem" }}>{dayName}</span>
                  <span style={{ fontSize: "0.85rem", color: "#666" }}>{dateLabel}{isToday ? " · Today" : ""}</span>
                </button>
                <div style={{ padding: dayTasks.length === 0 ? "0.35rem 1rem" : "0.75rem 1rem", display: "flex", flexDirection: "column", gap: "0.5rem", minHeight: dayTasks.length === 0 ? 0 : 80 }}>
                  {dayTasks.length === 0 ? (
                    <p style={{ margin: 0, color: "#aaa", fontSize: "0.85rem", fontStyle: "italic" }}>No tasks</p>
                  ) : (
                    visibleTasks.map((task) => (
                      <button key={task.id} onClick={() => setViewTask(task)} style={{ display: "flex", alignItems: "center", width: "100%", boxSizing: "border-box", minHeight: 44, border: "1px solid #e7ebf0", borderLeft: `3px solid ${priorityStrip(task.priority)}`, borderRadius: 8, background: "#fff", textAlign: "left", padding: "10px 12px", cursor: "pointer", fontFamily: FONT, gap: "0.5rem" }}>
                        <PriorityDot priority={task.priority} />
                        <div style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: "0.9rem", color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{task.title}</div>
                        {task.time && <div style={{ fontSize: "0.78rem", color: "#666", flexShrink: 0 }}>{fmtTime(task.time)}</div>}
                      </button>
                    ))
                  )}
                  {hiddenCount > 0 && (
                    <button onClick={() => handleDayClick(date)} style={{ border: "none", background: "transparent", color: NAVY, fontWeight: 700, fontSize: "0.76rem", textAlign: "left", padding: 0, cursor: "pointer", fontFamily: FONT }}>
                      +{hiddenCount} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.75rem", minHeight: 0 }}>
          <div className="week-row" style={{ ...ROW, height: calPrefs.showWeekends ? 'calc(50% - 0.5rem)' : '100%' }}>
            {weekdays.map(date => <DayColumn key={date.toISOString().slice(0, 10)} {...colProps(date)} />)}
          </div>
          {calPrefs.showWeekends && (
            <div className="week-row" style={ROW}>
              <DayColumn key={saturday.toISOString().slice(0, 10)} {...colProps(saturday)} />
              <DayColumn key={sunday.toISOString().slice(0, 10)}   {...colProps(sunday)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WeekView;
